import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Send,
  ShieldCheck,
  Zap,
  Target,
  Sparkles,
  Lock,
  Activity,
  BookOpen,
  Trophy,
  Users,
  Clock,
  Calendar,
  ArrowRight,
} from 'lucide-react';
import {
  getGeo,
  getPublicSettings,
  track,
  captureAdsterraSubidFromUrl,
  captureFbclidFromUrl,
} from '../lib/api';
import { injectPixels, trackPixelEvent } from '../lib/pixels';
import TradingChartBackground from '../components/TradingChartBackground';
import StudentIncomeProof from '../components/StudentIncomeProof';
import SocialProofToast from '../components/SocialProofToast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const SITE_LOGO =
  'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

// Localized text packs
const BN = {
  kicker: 'ফ্রি টেলিগ্রাম অ্যাক্সেস',
  brand: 'ALVEEFX',
  tagline:
    'লাইভ ট্রেডিং কোর্স • লস রিকভারি সেশন • Quotex লিডারবোর্ডে নিয়মিত আমাদের মেম্বাররা — এশিয়ার টপ ট্রেডার এর কমিউনিটিতে যোগ দিন।',
  ctaBig: 'Join Telegram',
  limitedPill: 'সীমিত সময়ের জন্য এক্সেস ওপেন',
  redirectIn: 'টেলিগ্রামে রিডাইরেক্ট হচ্ছে',
  joinNow: 'এখনই যোগ দিন',
  pills: ['লাইভ চার্ট অ্যানালাইসিস', 'লাইভ লার্নিং সেশন', 'ফ্রি মার্কেট এডুকেশন'],
  stat1Label: 'কমিউনিটি মেম্বার',
  stat2Label: 'ফ্রেশ সিগন্যাল',
  stat3Label: 'ফ্রি কনটেন্ট',
  stat3Value: '১০০%',
  recentTitle: 'সাম্প্রতিক অ্যানালাইসিস রেজাল্ট',
  eduDisclaimer:
    'এটি চার্ট অ্যানালাইসিস ও মার্কেট কনসেপ্ট শেখার একটি এডুকেশনাল কমিউনিটি। কনটেন্ট শুধুমাত্র এডুকেশনাল উদ্দেশ্যে — কোনো ফাইন্যান্সিয়াল অ্যাডভাইস নয়। আমরা কোনো ইনভেস্টমেন্ট রিকমেন্ডেশন বা ফান্ড ম্যানেজমেন্ট করি না। সবসময় নিজে গবেষণা করে সিদ্ধান্ত নিন।',
  bigStat1Label: 'মেম্বার & বাড়ছে',
  bigStat2Label: 'অ্যাক্টিভ কমিউনিটি',
  bigStat3Label: 'ডেইলি সেশন',
  bigStat4Label: 'ফ্রি ফরএভার',
  bigStat4Value: '১০০%',
  faqTitle: 'প্রায়শই জিজ্ঞাসিত প্রশ্ন',
  faqs: [
    {
      q: 'এটা কি সত্যিই ১০০% ফ্রি?',
      a: 'হ্যাঁ। AlveeFx এর পাবলিক টেলিগ্রাম চ্যানেল সম্পূর্ণ ফ্রি। কোনো পেমেন্ট, লগইন বা রেজিস্ট্রেশন লাগবে না।',
    },
    {
      q: 'কোন মার্কেট ও টাইমফ্রেম কভার করা হয়?',
      a: 'Forex, Binary (Quotex / Pocket Option), Crypto — ১M, ৩M, ৫M, ১৫M টাইমফ্রেম সহ ডেইলি সেটআপ ও অ্যানালাইসিস।',
    },
    {
      q: 'লস রিকভারি সেশন কীভাবে কাজ করে?',
      a: 'যদি কোনো মেম্বার ট্রেডে লস করে, AlveeFx নিজে লাইভ সেশনে এসে কন্ট্রোল্ড স্ট্র্যাটেজি দিয়ে লস রিকভার করতে সাহায্য করেন।',
    },
    {
      q: 'এটা কি ফাইন্যান্সিয়াল অ্যাডভাইস?',
      a: 'না। এটা শুধুমাত্র এডুকেশনাল কমিউনিটি। কোনো ইনভেস্টমেন্ট রিকমেন্ডেশন আমরা দিই না — সব ফাইনাল ডিসিশন আপনার নিজের।',
    },
    {
      q: 'আমি কীভাবে চার্ট স্টাডিগুলো ফলো করব?',
      a: 'শুধু টেলিগ্রাম চ্যানেলে জয়েন করুন — সব লেসন, চার্ট ও আপডেট রিয়েল-টাইমে পেয়ে যাবেন।',
    },
  ],
  finalKicker: 'মিস করবেন না',
  finalTitle: 'শেখা শুরু করতে প্রস্তুত?',
  finalSub: 'হাজারো মেম্বার ইতিমধ্যে যোগ দিয়েছে। ১০০% ফ্রি।',
  finalCta: 'Join Telegram',
  finalFree: '১০০% ফ্রি — এখনই যোগ দিন',
  privacyTitle: 'প্রাইভেসি পলিসি',
  footer:
    'ট্রেডিং-এ ঝুঁকি আছে। যে পরিমাণ মূলধন হারাতে পারেন, শুধু সেই পরিমাণ দিয়েই ট্রেড করুন।',
};

const EN = {
  kicker: 'FREE TELEGRAM ACCESS',
  brand: 'ALVEEFX',
  tagline:
    'Live trading courses · Loss-recovery sessions · Members ranking regularly on the Quotex leaderboard — join Asia’s top trader community.',
  ctaBig: 'Join Telegram',
  limitedPill: 'Limited-time access open',
  redirectIn: 'Redirecting to Telegram in',
  joinNow: 'Join Now',
  pills: ['Live Chart Studies', 'Live Learning Sessions', 'Free Market Education'],
  stat1Label: 'Community Learners',
  stat2Label: 'Fresh Signals',
  stat3Label: 'Free Content',
  stat3Value: '100%',
  recentTitle: 'Recent Analysis Results',
  eduDisclaimer:
    'This is an educational community for learning chart analysis and market concepts. Content is for educational purposes only — not financial advice. We do not provide investment recommendations or manage funds. Always do your own research.',
  bigStat1Label: 'Members & growing',
  bigStat2Label: 'Active Community',
  bigStat3Label: 'Daily Sessions',
  bigStat4Label: 'Free Forever',
  bigStat4Value: '100%',
  faqTitle: 'Frequently Asked Questions',
  faqs: [
    {
      q: 'Is it really 100% free?',
      a: 'Yes. AlveeFx’s public Telegram channel is completely free. No payment, no login, no registration required.',
    },
    {
      q: 'What markets and timeframes do you cover?',
      a: 'Forex, Binary (Quotex / Pocket Option), Crypto — across 1M, 3M, 5M and 15M timeframes with daily setups and analysis.',
    },
    {
      q: 'How do loss-recovery sessions work?',
      a: 'If a member faces losses on trades, AlveeFx hosts a live session and walks through a controlled strategy to help recover step by step.',
    },
    {
      q: 'Is this financial advice?',
      a: 'No. This is an educational community only. We do not provide investment recommendations — every decision you make is your own responsibility.',
    },
    {
      q: 'How do I follow the chart studies?',
      a: 'Just join the Telegram channel — all lessons, charts and updates are posted in real time.',
    },
  ],
  finalKicker: 'Don’t miss out',
  finalTitle: 'Ready to Start Learning?',
  finalSub: 'Thousands of members already joined. 100% free.',
  finalCta: 'Join Telegram',
  finalFree: 'Join now — it’s 100% free',
  privacyTitle: 'Privacy Policy',
  footer:
    'Trading involves risk. Only trade with capital you can afford to lose.',
};

// Realistic-looking marquee items (mixed pairs / timeframes / signals)
const MARQUEE_ITEMS = [
  { pair: 'EUR/USD', dir: 'Bullish', tf: '5m' },
  { pair: 'GBP/JPY', dir: 'Bearish', tf: '1m' },
  { pair: 'USD/CHF', dir: 'Bullish', tf: '3m' },
  { pair: 'AUD/USD', dir: 'Bearish', tf: '5m' },
  { pair: 'EUR/GBP', dir: 'Bullish', tf: '1m' },
  { pair: 'USD/JPY', dir: 'Bearish', tf: '3m' },
  { pair: 'GBP/USD', dir: 'Bullish', tf: '5m' },
  { pair: 'NZD/USD', dir: 'Bearish', tf: '1m' },
  { pair: 'EUR/JPY', dir: 'Bullish', tf: '3m' },
  { pair: 'AUD/JPY', dir: 'Bearish', tf: '5m' },
  { pair: 'USD/CAD', dir: 'Bullish', tf: '1m' },
  { pair: 'CHF/JPY', dir: 'Bearish', tf: '3m' },
];

/**
 * /join — Champads-style landing for Facebook ad traffic.
 *
 * - Bilingual: BD geo → full Bangla; others → English (English copy admin-editable).
 * - 30-second auto-redirect timer to Telegram (cancels on any join click).
 * - Section list: hero with portrait + redirect countdown, trust pills, stats,
 *   results marquee, big stats grid, FAQ accordion, final CTA, privacy, footer.
 */
export default function JoinPage() {
  const [settings, setSettings] = useState(null);
  const [geo, setGeo] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [redirectCancelled, setRedirectCancelled] = useState(false);
  const intervalRef = useRef(null);
  const trackedRedirectRef = useRef(false);

  // Load settings + geo + pixels + visit track
  useEffect(() => {
    captureAdsterraSubidFromUrl();
    captureFbclidFromUrl();
    (async () => {
      try {
        const [s, g] = await Promise.all([getPublicSettings(), getGeo()]);
        setSettings(s);
        setGeo(g);
        injectPixels({
          facebook_pixel_id: s.facebook_pixel_id,
          tiktok_pixel_id: s.tiktok_pixel_id,
          google_ads_id: s.google_ads_id,
          google_analytics_id: s.google_analytics_id,
        });
        const params = new URLSearchParams(window.location.search);
        track({
          type: 'visit',
          country_code: g?.country_code || null,
          country_name: g?.country_name || null,
          source: 'join_page',
          utm_source: params.get('utm_source'),
          utm_campaign: params.get('utm_campaign'),
          utm_medium: params.get('utm_medium'),
          referrer: typeof document !== 'undefined' ? document.referrer : null,
        });
        // Initialize redirect countdown
        const sec = Number(s.join_page_redirect_seconds) || 30;
        setSecondsLeft(sec);
      } catch (e) {
        console.error('join page load error', e);
      }
    })();
  }, []);

  // Force Bangla for the entire /join page (user request).
  // We still keep the geo detection above for the `lang` HTML attribute + analytics,
  // but every visible string is now the BN pack.
  const isBangla = true;
  const T = BN;

  const tg = settings?.join_page_telegram_link || settings?.telegram_link || '#';

  // Countdown tick
  useEffect(() => {
    if (secondsLeft == null || redirectCancelled) return;
    if (secondsLeft <= 0) {
      // Auto redirect (only once)
      if (!trackedRedirectRef.current && tg && tg !== '#') {
        trackedRedirectRef.current = true;
        try {
          trackPixelEvent('Subscribe', { source: 'join_page_auto' });
          track({
            type: 'tg_click',
            country_code: geo?.country_code || null,
            country_name: geo?.country_name || null,
            source: 'join_page_auto',
          });
        } catch {}
        window.location.href = tg;
      }
      return;
    }
    intervalRef.current = setTimeout(() => {
      setSecondsLeft((v) => (v == null ? null : v - 1));
    }, 1000);
    return () => intervalRef.current && clearTimeout(intervalRef.current);
  }, [secondsLeft, redirectCancelled, tg, geo]);

  function handleJoin(source = 'join_page') {
    // Cancel auto-redirect since user took explicit action
    setRedirectCancelled(true);
    try {
      trackPixelEvent('Subscribe', { source });
      track({
        type: 'tg_click',
        country_code: geo?.country_code || null,
        country_name: geo?.country_name || null,
        source,
      });
    } catch {}
  }

  // Big stats (admin-configurable for the hero member count)
  const memberCount = settings?.join_page_member_count || '152000+';
  const yearsActive = settings?.join_page_years_active || '5+ Years';
  const dailyLessons = settings?.join_page_daily_lessons || 'Daily';
  const profileImg = settings?.join_page_profile_image_url || SITE_LOGO;

  // Bilingual translate utility for number-like values when Bangla (only for stat 4 = '100%')
  const stat3Value = T.stat3Value;
  const bigStat4Value = T.bigStat4Value;

  return (
    <main
      className="min-h-screen text-white bg-[hsl(0_0%_4%)] flex flex-col relative overflow-hidden noise-overlay"
      data-testid="join-page"
      lang={isBangla ? 'bn' : 'en'}
    >
      {/* Animated trading chart background (subtle, behind everything) */}
      <TradingChartBackground className="z-0" />

      {/* Ambient background orbs (golden left, blue right) */}
      <div className="join-ambient-orb orb-a" aria-hidden />
      <div className="join-ambient-orb orb-b" aria-hidden />

      {/* Floating social-proof "Someone just joined" popup */}
      <SocialProofToast isBangla={isBangla} logoUrl={profileImg || SITE_LOGO} />

      {/* Top bar */}
      <header className="relative z-10 border-b border-white/5">
        <div className="container-x py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5" data-testid="join-logo">
            <img
              src={SITE_LOGO}
              alt="AlveeFx"
              className="h-8 w-8 rounded-full object-cover ring-1 ring-[rgba(245,185,66,0.42)]"
            />
            <span className="font-display uppercase tracking-[0.18em] text-sm sm:text-base">
              AlveeFx
            </span>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative z-10 pt-10 pb-12 sm:pt-14 sm:pb-16">
        <div className="container-x max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[rgba(245,185,66,0.42)] bg-[rgba(245,185,66,0.08)] mb-5"
            data-testid="join-kicker"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(43_92%_56%)] animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] font-semibold">
              {T.kicker}
            </span>
          </motion.div>

          {/* Portrait with animated rotating gradient ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mx-auto mb-6 sm:mb-8 relative h-32 w-32 sm:h-40 sm:w-40"
          >
            <span className="portrait-ring absolute inset-0 rounded-full" aria-hidden />
            <div className="absolute inset-[6px] rounded-full overflow-hidden ring-2 ring-[hsl(43_92%_56%)]/70 shadow-[0_24px_56px_-16px_rgba(245,185,66,0.5)]">
              <img
                src={profileImg}
                alt="AlveeFx portrait"
                className="h-full w-full object-cover"
                data-testid="join-portrait"
              />
            </div>
            {/* LIVE ONLINE badge */}
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[rgba(10,10,14,0.9)] backdrop-blur border border-[hsl(43_92%_56%)]/50 px-2.5 py-1">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inset-0 rounded-full bg-[hsl(140_70%_50%)] animate-ping opacity-70" />
                <span className="relative rounded-full h-1.5 w-1.5 bg-[hsl(140_70%_50%)]" />
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[hsl(43_92%_72%)]">LIVE ONLINE</span>
            </div>
          </motion.div>

          {/* Massive brand title with gradient */}
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="font-display uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl bg-clip-text text-transparent"
            style={{
              backgroundImage:
                'linear-gradient(180deg, #fff 0%, #f5b942 50%, #c98a1a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            data-testid="join-title"
          >
            {T.brand}
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="mt-4 text-base sm:text-lg text-[hsl(0_0%_82%)] leading-relaxed max-w-2xl mx-auto"
            data-testid="join-subtitle"
          >
            {T.tagline}
          </motion.p>

          {/* HURRY UP animated progress strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="mt-6 mx-auto max-w-sm rounded-lg border border-[rgba(225,29,46,0.5)] bg-[rgba(225,29,46,0.06)] px-3.5 py-2 relative overflow-hidden"
          >
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inset-0 rounded-full bg-[hsl(355_75%_55%)] animate-ping opacity-75" />
                <span className="relative rounded-full h-2 w-2 bg-[hsl(355_75%_55%)]" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(355_75%_75%)] font-bold">
                🔥 Join Fast · Hurry Up
              </span>
            </div>
            <div className="mt-1.5 text-[10px] text-muted-foreground">
              দ্রুত যোগ দিন — সিট শেষ হয়ে যাচ্ছে
            </div>
            {/* Sliding red progress bar */}
            <div className="absolute inset-x-0 bottom-0 h-0.5 bg-[rgba(225,29,46,0.15)] overflow-hidden">
              <span className="hurry-progress absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-[hsl(355_75%_55%)] to-transparent" />
            </div>
          </motion.div>

          {/* Big CTA — Telegram-blue button + pointing hand */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.32 }}
            className="mt-7 relative inline-block"
          >
            <a
              href={tg}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleJoin('join_page_hero')}
              data-testid="join-cta-button"
              className="join-tg-btn inline-flex items-center justify-center gap-2.5 h-14 px-8 sm:px-12 rounded-full text-white font-bold text-base sm:text-lg transition-all"
            >
              <Send className="h-5 w-5" />
              {T.ctaBig}
              <ArrowRight className="h-5 w-5" />
            </a>
            {/* Animated pointing hand emoji */}
            <span
              className="pointing-hand absolute -right-2 sm:-right-4 -bottom-3 sm:-bottom-4 text-3xl sm:text-4xl select-none pointer-events-none"
              aria-hidden
            >
              👆
            </span>
          </motion.div>

          {/* Auto-redirect countdown */}
          {secondsLeft != null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="mt-5 text-sm text-muted-foreground inline-flex items-center gap-2"
              data-testid="join-redirect-countdown"
            >
              <Clock className="h-3.5 w-3.5 text-[hsl(43_92%_56%)]" />
              {redirectCancelled ? (
                <span className="text-[hsl(43_92%_56%)]">
                  {isBangla ? 'অটো-রিডাইরেক্ট বাতিল হয়েছে' : 'Auto-redirect cancelled'}
                </span>
              ) : (
                <span>
                  {T.redirectIn}{' '}
                  <span className="text-[hsl(43_92%_56%)] font-bold">{secondsLeft}s</span>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => {
                      setRedirectCancelled(true);
                      handleJoin('join_page_inline');
                      window.open(tg, '_blank', 'noopener,noreferrer');
                    }}
                    className="text-[hsl(43_92%_56%)] underline hover:text-[hsl(43_92%_64%)] font-semibold"
                    data-testid="join-now-link"
                  >
                    {T.joinNow}
                  </button>
                </span>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* STUDENT INCOME PROOF — YouTube carousel with autoplay + swipe */}
      <StudentIncomeProof isBangla={isBangla} />

      {/* TRUST PILLS */}
      <section className="relative z-10 pb-10">
        <div className="container-x max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: Activity, label: T.pills[0] },
              { icon: BookOpen, label: T.pills[1] },
              { icon: Sparkles, label: T.pills[2] },
            ].map((p, i) => (
              <div
                key={i}
                className="rounded-xl card-surface gold-hairline px-4 py-3 flex items-center justify-center gap-2 text-sm font-semibold"
                data-testid={`join-trust-pill-${i}`}
              >
                <p.icon className="h-4 w-4 text-[hsl(43_92%_56%)]" />
                <span>{p.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS ROW (3) */}
      <section className="relative z-10 pb-10">
        <div className="container-x max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { value: memberCount, label: T.stat1Label },
              { value: dailyLessons, label: T.stat2Label },
              { value: stat3Value, label: T.stat3Label },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-xl card-surface gold-hairline p-4 sm:p-5 text-center"
                data-testid={`join-stat-${i}`}
              >
                <div className="font-display text-2xl sm:text-3xl text-[hsl(43_92%_56%)]">
                  {s.value}
                </div>
                <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDUCATIONAL DISCLAIMER */}
      <section className="relative z-10 pb-10">
        <div className="container-x max-w-3xl mx-auto">
          <div
            className="rounded-xl border border-[rgba(245,185,66,0.22)] bg-[rgba(245,185,66,0.04)] p-4 sm:p-5 text-center"
            data-testid="join-edu-disclaimer"
          >
            <div className="inline-flex items-center gap-1.5 mb-2">
              <BookOpen className="h-3.5 w-3.5 text-[hsl(43_92%_56%)]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] font-semibold">
                {isBangla ? 'এডুকেশনাল নোটিশ' : 'Educational Notice'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-[hsl(0_0%_78%)] leading-relaxed">
              {T.eduDisclaimer}
            </p>
          </div>
        </div>
      </section>

      {/* MARQUEE — Recent Analysis */}
      <section className="relative z-10 pb-12 border-t border-b border-white/5 bg-[hsl(0_0%_3%)]/60">
        <div className="container-x max-w-5xl mx-auto py-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] font-semibold text-center mb-4">
            {T.recentTitle}
          </div>
          <div
            className="relative overflow-hidden marquee-mask"
            data-testid="join-marquee"
          >
            <motion.div
              className="flex gap-3 whitespace-nowrap"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 38, ease: 'linear', repeat: Infinity }}
              style={{ width: 'max-content' }}
            >
              {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((it, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-white/[0.03] text-xs"
                >
                  <span className="font-mono font-bold">{it.pair}</span>
                  <span
                    className={
                      it.dir === 'Bullish'
                        ? 'text-[hsl(140_70%_60%)] font-semibold'
                        : 'text-[hsl(355_75%_64%)] font-semibold'
                    }
                  >
                    {it.dir}
                  </span>
                  <span className="text-muted-foreground">{it.tf}</span>
                  <span className="inline-flex items-center gap-1 text-[hsl(140_70%_60%)] font-semibold">
                    <ShieldCheck className="h-3 w-3" />
                    {isBangla ? 'সঠিক' : 'Correct'}
                  </span>
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* BIG STATS GRID (4) */}
      <section className="relative z-10 py-12">
        <div className="container-x max-w-5xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { Icon: Users, value: memberCount, label: T.bigStat1Label },
              { Icon: Calendar, value: yearsActive, label: T.bigStat2Label },
              { Icon: Target, value: dailyLessons, label: T.bigStat3Label },
              { Icon: Trophy, value: bigStat4Value, label: T.bigStat4Label },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-xl card-surface gold-hairline p-5 text-center"
                data-testid={`join-bigstat-${i}`}
              >
                <s.Icon className="h-5 w-5 text-[hsl(43_92%_56%)] mx-auto mb-2" />
                <div className="font-display text-2xl sm:text-3xl text-white">
                  {s.value}
                </div>
                <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-12 border-t border-white/5">
        <div className="container-x max-w-3xl mx-auto">
          <h2
            className="font-display uppercase text-2xl sm:text-3xl text-center mb-7"
            data-testid="join-faq-title"
          >
            {T.faqTitle}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {T.faqs.map((it, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="rounded-xl border border-white/10 bg-white/[0.02] px-4 sm:px-5"
                data-testid={`join-faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold hover:no-underline py-4">
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[hsl(0_0%_82%)] leading-relaxed pb-4">
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 py-14 border-t border-white/5 bg-gradient-to-b from-[hsl(0_0%_4%)] to-[hsl(0_0%_2%)]">
        <div className="container-x max-w-2xl mx-auto text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(355_75%_60%)] font-semibold mb-3">
            {T.finalKicker}
          </div>
          <h2 className="font-display uppercase text-3xl sm:text-5xl leading-tight mb-3">
            {T.finalTitle}
          </h2>
          <p className="text-sm sm:text-base text-[hsl(0_0%_82%)] mb-6 leading-relaxed">
            {T.finalSub}
          </p>
          <div className="relative inline-block">
            <a
              href={tg}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleJoin('join_page_final')}
              data-testid="join-final-cta"
              className="join-tg-btn inline-flex items-center justify-center gap-2.5 h-14 px-10 sm:px-14 rounded-full text-white font-bold text-base sm:text-lg transition-all"
            >
              <Send className="h-5 w-5" />
              {T.finalCta}
              <ArrowRight className="h-5 w-5" />
            </a>
            <span
              className="pointing-hand absolute -right-2 sm:-right-4 -bottom-3 sm:-bottom-4 text-3xl sm:text-4xl select-none pointer-events-none"
              aria-hidden
            >
              👆
            </span>
          </div>
          <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            {T.finalFree}
          </p>
        </div>
      </section>

      {/* PRIVACY */}
      <section className="relative z-10 border-t border-white/5">
        <div className="container-x py-8 max-w-2xl mx-auto" data-testid="join-privacy">
          <div className="inline-flex items-center gap-2 mb-3">
            <Lock className="h-3.5 w-3.5 text-[hsl(43_92%_56%)]" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] font-semibold">
              {T.privacyTitle}
            </span>
          </div>
          <p
            className="text-sm text-[hsl(0_0%_75%)] leading-relaxed"
            data-testid="join-privacy-text"
          >
            {isBangla
              ? settings?.join_page_privacy_bn ||
                'আমাদের ওয়েবসাইট আপনার কোনো ব্যক্তিগত তথ্য সংগ্রহ করে না।'
              : settings?.join_page_privacy_en ||
                'Our website does not collect any personal information from you.'}
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 border-t border-white/5 py-5 text-center">
        <p
          className="text-[11px] text-muted-foreground max-w-2xl mx-auto px-4 leading-snug"
          data-testid="join-footer-text"
        >
          {T.footer}
        </p>
      </footer>

      {/* Local styles for marquee fade mask + animations */}
      <style>{`
        .marquee-mask {
          -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%);
                  mask-image: linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%);
        }
        /* Telegram-blue CTA (matches Telegram brand color) */
        .join-tg-btn {
          background: linear-gradient(180deg, #2AABEE 0%, #229ED9 100%);
          box-shadow:
            0 18px 44px -12px rgba(34, 158, 217, 0.55),
            inset 0 1px 0 rgba(255,255,255,0.18);
          position: relative;
          overflow: hidden;
        }
        .join-tg-btn:hover {
          background: linear-gradient(180deg, #3AB6F5 0%, #2AABEE 100%);
          box-shadow:
            0 22px 52px -10px rgba(34, 158, 217, 0.75),
            inset 0 1px 0 rgba(255,255,255,0.22);
          transform: translateY(-1px);
        }
        .join-tg-btn:active { transform: translateY(0); }
        .join-tg-btn::after {
          content: '';
          position: absolute;
          top: 0; left: -80%;
          width: 60%; height: 100%;
          background: linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.30) 50%, transparent 80%);
          transform: skewX(-20deg);
          animation: joinTgSheen 3.6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes joinTgSheen {
          0%   { left: -80%; }
          55%  { left: 130%; }
          100% { left: 130%; }
        }

        /* Portrait rotating conic-gradient ring */
        .portrait-ring {
          background: conic-gradient(
            from 0deg,
            rgba(245,185,66,0.05) 0deg,
            rgba(245,185,66,0.9) 60deg,
            rgba(255,220,140,1) 90deg,
            rgba(245,185,66,0.4) 180deg,
            rgba(245,185,66,0.05) 240deg,
            rgba(245,185,66,0.05) 360deg
          );
          animation: portraitSpin 6s linear infinite;
          filter: blur(0.5px);
        }
        @keyframes portraitSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* Sliding red progress bar under HURRY UP pill */
        .hurry-progress {
          animation: hurrySlide 2.4s ease-in-out infinite;
        }
        @keyframes hurrySlide {
          0%   { left: -33%; }
          100% { left: 100%; }
        }

        /* Bouncing pointing hand next to CTA */
        .pointing-hand {
          animation: handTap 1.3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          transform-origin: 60% 40%;
          filter: drop-shadow(0 6px 8px rgba(0,0,0,0.5));
        }
        @keyframes handTap {
          0%, 100% { transform: translate(0, 0) rotate(-8deg); }
          40%      { transform: translate(-6px, -8px) rotate(-20deg); }
          60%      { transform: translate(-2px, -2px) rotate(-14deg); }
        }

        /* Ambient background orbs */
        .join-ambient-orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(80px);
          opacity: 0.32;
          pointer-events: none;
          animation: orbFloat 14s ease-in-out infinite;
        }
        .join-ambient-orb.orb-a {
          top: -140px;
          left: -140px;
          width: 460px;
          height: 460px;
          background: radial-gradient(circle at center, rgba(245,185,66,0.85), transparent 65%);
        }
        .join-ambient-orb.orb-b {
          top: -80px;
          right: -160px;
          width: 520px;
          height: 520px;
          background: radial-gradient(circle at center, rgba(42,171,238,0.55), transparent 65%);
          animation-delay: -7s;
        }
        @keyframes orbFloat {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50%      { transform: translate3d(20px, 30px, 0) scale(1.05); }
        }
      `}</style>
    </main>
  );
}
